/**
 * AdminAcceptInvite — iter83 (P1 Task 1)
 *
 * Public landing page for tenant-admin magic-link invitations. Token
 * is in the URL; the user picks a password (and optionally edits their
 * display name) and on success is redirected to /admin to sign in.
 */
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Shield, KeyRound, Check, ArrowRight, Building2 } from "lucide-react";
import { api } from "@/lib/api";

export default function AdminAcceptInvite() {
    const { token } = useParams();
    const navigate = useNavigate();
    const [name, setName] = useState("");
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(null);

    // Hide accept page from search engines.
    useEffect(() => {
        const meta = document.createElement("meta");
        meta.name = "robots";
        meta.content = "noindex, nofollow, noarchive";
        document.head.appendChild(meta);
        return () => { document.head.removeChild(meta); };
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        if (password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
        if (password !== confirm) { toast.error("Passwords don't match"); return; }
        setBusy(true);
        try {
            const r = await api.acceptAdminInvite(token, password, name.trim() || undefined);
            setDone(r);
            toast.success("Account ready — sign in to continue");
            setTimeout(() => navigate("/admin"), 1800);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Invite invalid or expired");
        }
        setBusy(false);
    };

    return (
        <div className="min-h-screen flex items-center justify-center px-4">
            <div className="brutal-card p-7 lg:p-10 w-full max-w-md" data-testid="admin-accept-card">
                <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 bg-cyber border-2 border-black shadow-brutal flex items-center justify-center">
                        <Shield className="w-5 h-5 text-black" strokeWidth={2.5} />
                    </div>
                    <div>
                        <p className="text-[10px] uppercase tracking-widest text-zinc-500">Site Admin invitation</p>
                        <h1 className="font-display font-black text-2xl">Set up your account</h1>
                    </div>
                </div>

                {!done ? (
                    <form onSubmit={submit} className="space-y-4">
                        <div>
                            <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">
                                Display name (optional)
                            </label>
                            <input
                                data-testid="admin-accept-name-input"
                                type="text" value={name}
                                onChange={(e) => setName(e.target.value)}
                                className="brutal-input w-full"
                                placeholder="Jane Coach"
                                autoFocus
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">
                                Choose password
                            </label>
                            <input
                                data-testid="admin-accept-pw-input"
                                type="password" value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="brutal-input w-full"
                                placeholder="At least 8 characters"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">
                                Confirm
                            </label>
                            <input
                                data-testid="admin-accept-pw-confirm-input"
                                type="password" value={confirm}
                                onChange={(e) => setConfirm(e.target.value)}
                                className="brutal-input w-full"
                            />
                        </div>
                        <button
                            data-testid="admin-accept-submit-btn"
                            type="submit" disabled={busy}
                            className="brutal-btn-primary w-full disabled:opacity-60"
                        >
                            {busy ? "Setting up..." : "Activate account"} <KeyRound className="w-4 h-4" />
                        </button>
                    </form>
                ) : (
                    <div className="text-center space-y-3" data-testid="admin-accept-success-state">
                        <div className="w-12 h-12 mx-auto bg-cyber border-2 border-black shadow-brutal flex items-center justify-center">
                            <Check className="w-6 h-6 text-black" strokeWidth={2.5} />
                        </div>
                        <h3 className="font-display font-black text-xl">You're in.</h3>
                        {done.tenant_name && (
                            <p className="text-sm text-zinc-400 flex items-center justify-center gap-1.5">
                                <Building2 className="w-3.5 h-3.5 text-cyber" />
                                Site Admin on <strong className="text-white">{done.tenant_name}</strong>
                            </p>
                        )}
                        <p className="text-sm text-zinc-400">Redirecting to sign in…</p>
                        <button
                            onClick={() => navigate("/admin")}
                            className="brutal-btn-ghost text-xs"
                            data-testid="admin-accept-go-signin"
                        >
                            Sign in now <ArrowRight className="w-3 h-3" />
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
